import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Criticality/Risk level utilities
export const criticalityConfig = {
  critica: { label: 'Crítica', color: 'bg-red-50 text-red-700 border-red-200', dotColor: 'bg-red-500' },
  alta: { label: 'Alta', color: 'bg-orange-50 text-orange-700 border-orange-200', dotColor: 'bg-orange-500' },
  media: { label: 'Média', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', dotColor: 'bg-yellow-500' },
  baixa: { label: 'Baixa', color: 'bg-green-50 text-green-700 border-green-200', dotColor: 'bg-green-500' },
};

export const getCriticalityBadge = (level) => {
  return criticalityConfig[level] || criticalityConfig.media;
};

// Asset type utilities
export const assetTypeConfig = {
  hardware: { label: 'Hardware', icon: 'Server', color: 'text-blue-600' },
  software: { label: 'Software', icon: 'Code', color: 'text-purple-600' },
  dados: { label: 'Dados', icon: 'Database', color: 'text-green-600' },
  servico: { label: 'Serviço', icon: 'Cloud', color: 'text-cyan-600' },
  pessoas: { label: 'Pessoas', icon: 'Users', color: 'text-orange-600' },
  instalacao: { label: 'Instalação', icon: 'Building', color: 'text-slate-600' },
};

export const getAssetTypeConfig = (type) => {
  return assetTypeConfig[type] || { label: type, icon: 'Box', color: 'text-slate-600' };
};

// Status utilities
export const statusConfig = {
  ativo: { label: 'Ativo', color: 'bg-green-50 text-green-700 border-green-200' },
  inativo: { label: 'Inativo', color: 'bg-slate-50 text-slate-600 border-slate-200' },
  manutencao: { label: 'Manutenção', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  descontinuado: { label: 'Descontinuado', color: 'bg-red-50 text-red-700 border-red-200' },
};

export const getStatusConfig = (status) => {
  return statusConfig[status] || statusConfig.ativo;
};

// Task status utilities
export const taskStatusConfig = {
  a_fazer: { label: 'A Fazer', color: 'bg-slate-50 text-slate-700 border-slate-200' },
  em_progresso: { label: 'Em Progresso', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  bloqueada: { label: 'Bloqueada', color: 'bg-red-50 text-red-700 border-red-200' },
  em_revisao: { label: 'Em Revisão', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  concluida: { label: 'Concluída', color: 'bg-green-50 text-green-700 border-green-200' },
  cancelada: { label: 'Cancelada', color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

export const getTaskStatusConfig = (status) => {
  return taskStatusConfig[status] || taskStatusConfig.a_fazer;
};

// Priority utilities
export const priorityConfig = {
  urgente: { label: 'Urgente', color: 'bg-red-50 text-red-700 border-red-200' },
  alta: { label: 'Alta', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  media: { label: 'Média', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  baixa: { label: 'Baixa', color: 'bg-green-50 text-green-700 border-green-200' },
};

export const getPriorityConfig = (priority) => {
  return priorityConfig[priority] || priorityConfig.media;
};

// Compliance status utilities
export const complianceStatusConfig = {
  conforme: { label: 'Conforme', color: 'bg-green-50 text-green-700 border-green-200' },
  nao_conforme: { label: 'Não Conforme', color: 'bg-red-50 text-red-700 border-red-200' },
  em_progresso: { label: 'Em Progresso', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  nao_aplicavel: { label: 'Não Aplicável', color: 'bg-slate-50 text-slate-600 border-slate-200' },
  nao_avaliado: { label: 'Não Avaliado', color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

export const getComplianceStatusConfig = (status) => {
  return complianceStatusConfig[status] || complianceStatusConfig.nao_avaliado;
};

// Role utilities
export const roleConfig = {
  admin: { label: 'Administrador', color: 'bg-purple-50 text-purple-700' },
  diretor: { label: 'Direção', color: 'bg-blue-50 text-blue-700' },
  ciso: { label: 'CISO', color: 'bg-indigo-50 text-indigo-700' },
  spoc: { label: 'SPOC', color: 'bg-cyan-50 text-cyan-700' },
  gestor_ativos: { label: 'Gestor de Ativos', color: 'bg-green-50 text-green-700' },
  responsavel_area: { label: 'Responsável de Área', color: 'bg-amber-50 text-amber-700' },
  auditor: { label: 'Auditor', color: 'bg-slate-50 text-slate-700' },
  colaborador: { label: 'Colaborador', color: 'bg-slate-50 text-slate-600' },
  ai_officer: { label: 'AI Officer', color: 'bg-fuchsia-50 text-fuchsia-700' },
};

export const roleOptions = [
  { value: 'admin', label: 'Administrador' },
  { value: 'diretor', label: 'Direção' },
  { value: 'ciso', label: 'CISO' },
  { value: 'ai_officer', label: 'AI Officer' },
  { value: 'spoc', label: 'SPOC' },
  { value: 'gestor_ativos', label: 'Gestor de Ativos' },
  { value: 'responsavel_area', label: 'Responsável de Área' },
  { value: 'auditor', label: 'Auditor' },
  { value: 'colaborador', label: 'Colaborador' },
];

export const getRoleConfig = (role) => {
  return roleConfig[role] || roleConfig.colaborador;
};

// Date formatting
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-PT', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-PT', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Number formatting
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('pt-PT').format(num);
};

export const formatCurrency = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR'
  }).format(num);
};

// Percentage formatting
export const formatPercentage = (num, decimals = 1) => {
  if (num === null || num === undefined) return '-';
  return `${num.toFixed(decimals)}%`;
};

// Chart colors
export const chartColors = {
  primary: '#2563eb',
  secondary: '#64748b',
  success: '#22c55e',
  warning: '#eab308',
  danger: '#ef4444',
  info: '#0ea5e9',
  purple: '#8b5cf6',
};

export const criticalityChartColors = {
  critica: '#ef4444',
  alta: '#f97316',
  media: '#eab308',
  baixa: '#22c55e',
};

export const assetTypeChartColors = {
  hardware: '#3b82f6',
  software: '#8b5cf6',
  dados: '#22c55e',
  servico: '#06b6d4',
  pessoas: '#f97316',
  instalacao: '#64748b',
};
